'use client'

// Every Ordinals Wallet market, searched as one list: the alkanes on the AMM
// and every collection, rune, BRC-20 and TAP market the site lists.
//
// This is what a `$cashtag` resolves against while someone writes, and what
// the market picker offers. With nothing typed it IS the homepage's global
// market data — the same endpoint, ranking and order as the table there, so
// the top of this list and the top of that table are the same markets in the
// same order. Alkanes are a much smaller, in-memory feed, so they join the
// moment a query can tell them apart. Collections past that ranking come from
// the same server search the command palette uses.

import React from 'react'
import useSwr from 'swr'
import { API_URL } from '@/utils/constants'
import { fetcher } from '@/utils/fetcher'
import useDebounce from '@/utils/hooks/useDebounce'
import { COLLECTIONS_VERSION } from '@/utils/constants'
import collectionIconUrl from '@/utils/collection-icon-url'
import { OW_NAMESPACE, collectionSmartTag } from './collection-tag'
import { ALKANE_ASSETS_KEY, fetchAlkaneAssets } from './use-market-badge'

/// Below this a query is noise; the market ranking serves better.
const MIN_QUERY = 2
const PER_SOURCE = 12
/// Anyone can mint an alkane and call it after a famous ticker. Searching
/// "ordi" put one holding $0.00000000014 of liquidity above BRC-20 ORDI,
/// which traded 0.0156 BTC that week, because it came from the alkanes list
/// rather than the collections list. Below this floor an alkane sorts after
/// the collections instead of ahead of them; the name alone is not a market.
const MIN_ALKANE_LIQUIDITY_USD = 100

/// The homepage's own market table: same endpoint, same ranking, same order.
const RANKED_MARKETS = `${API_URL}/${COLLECTIONS_VERSION}?limit=200&order=GlobalVolumeWeekDesc`

export interface MarketOption {
	key: string
	/// Dollars in the pool, for an alkane. Not shown — it decides order.
	liquidityUsd?: number | null
	namespace: string
	id: string
	/// The cashtag it is written as.
	symbol: string
	/// What it is called (the collection's or token's own name).
	name: string
	icon: string | null
	/// Its identity, shown under the name.
	detail: string
}

interface CollectionRow {
	name?: string | null
	slug?: string | null
	icon?: string | null
	icon_inscription?: { id?: string | null } | null
}

function collectionOption(row: CollectionRow): MarketOption | null {
	const slug = row.slug?.trim()
	if (!slug) return null
	const tag = collectionSmartTag(slug)
	if (!tag) return null
	return {
		key: `${OW_NAMESPACE}:${tag.id}`,
		namespace: OW_NAMESPACE,
		id: tag.id,
		symbol: tag.symbol,
		name: row.name?.trim() || slug,
		// By the site's own rules, so a market wears the same mark here as in
		// the homepage's table.
		icon: collectionIconUrl(row),
		detail: slug
	}
}

const has = (haystack: string, needle: string) => haystack.toLowerCase().includes(needle)

/// Markets matching what has been typed so far. `query` is the cashtag's text
/// without the `$` — null when no cashtag is being written, which asks for
/// nothing at all.
export default function useMarketSearch(query: string | null): {
	options: MarketOption[]
	loading: boolean
} {
	const needle = (query ?? '').trim().toLowerCase()
	const active = query !== null
	const debounced = useDebounce(needle, 200)
	// Only the server can answer past the popular list, and only for a query
	// that has settled — a request per keystroke would arrive out of order.
	const searching = active && debounced.length >= MIN_QUERY && debounced === needle

	const { data: assets } = useSwr(active ? ALKANE_ASSETS_KEY : null, fetchAlkaneAssets, {
		revalidateOnFocus: false,
		shouldRetryOnError: false
	})
	const { data: popular } = useSwr(active ? RANKED_MARKETS : null, fetcher, {
		revalidateOnFocus: false,
		dedupingInterval: 300_000,
		shouldRetryOnError: false
	})
	const { data: found, isLoading: finding } = useSwr(
		searching
			? `${API_URL}/v2/search/${encodeURIComponent(debounced)}?limit=${PER_SOURCE}&t=1&include_collection_objects=true&group_collection_objects=true`
			: null,
		fetcher,
		{ revalidateOnFocus: false, shouldRetryOnError: false, keepPreviousData: true }
	)

	const options = React.useMemo<MarketOption[]>(() => {
		if (!active) return []
		const rows: CollectionRow[] = searching
			? ((found as { collections?: CollectionRow[] } | undefined)?.collections ?? [])
			: ((popular as { collections?: CollectionRow[] } | undefined)?.collections ?? []).filter(
					(row) => !needle || has(row.name ?? '', needle) || has(row.slug ?? '', needle)
			  )
		const collections = rows
			.map(collectionOption)
			.filter((option): option is MarketOption => option !== null)
			.slice(0, PER_SOURCE)

		// Nothing typed: the resting list is the market itself — top collections
		// by volume, in the site's own ranking. An alkane is named, not browsed
		// to, so the AMM's tokens join once there is something to match.
		if (!needle) return collections

		const alkanes = [...(assets?.values() ?? [])]
			.filter((asset) => asset.name && /^[A-Za-z][A-Za-z0-9]{0,14}$/.test(asset.name))
			.map<MarketOption>((asset) => ({
				key: `alkanes:${asset.id}`,
				namespace: 'alkanes',
				id: asset.id,
				symbol: asset.name!.toUpperCase(),
				name: asset.name!,
				icon: asset.icon ?? null,
				detail: `alkanes:${asset.id}`,
				liquidityUsd: asset.liquidity_usd ?? null
			}))
			.filter((option) => has(option.symbol, needle) || has(option.id, needle))
			.slice(0, PER_SOURCE)

		// Whatever the typed text names exactly leads, whichever list it came
		// from — someone typing "DIESEL" in full means that one. Among equals,
		// substance decides: an alkane with no pool behind its name drops below
		// the collections rather than riding its ticker to the top.
		// An alkane is listed twice: once by the AMM and once as the collection
		// OW wraps it in (`alkane-2:142`) — same market, same destination. The
		// AMM's row is the canonical one, so the wrapper drops out. (With no
		// query there are no AMM rows to duplicate, and the wrapper is the only
		// way that market appears at all.)
		const fromAmm = new Set(alkanes.map((option) => option.id))
		const deduped = collections.filter((option) => {
			const wrapped = /^alkane-(.+)$/.exec(option.detail)?.[1]
			return !wrapped || !fromAmm.has(wrapped)
		})

		const tier = (option: MarketOption) =>
			option.symbol.toLowerCase() === needle ? 0 : option.symbol.toLowerCase().startsWith(needle) ? 1 : 2
		const empty = (option: MarketOption) =>
			option.namespace === 'alkanes' && (option.liquidityUsd ?? 0) < MIN_ALKANE_LIQUIDITY_USD ? 1 : 0
		return [...alkanes, ...deduped].sort((a, b) => tier(a) - tier(b) || empty(a) - empty(b))
	}, [active, assets, popular, found, needle, searching])

	return { options, loading: searching && finding && options.length === 0 }
}
